<!-- eslint-disable vue/no-v-html -->
<template>
  <div class="load-more-articles">
    <slot v-if="!loaded" />
    <ul
      v-if="loaded"
      :class="['article-grid', {'article-grid--landing': landingArticles}]"
    >
      <li
        v-for="(article, i) in loadList"
        :key="i"
      >
        <a
          v-tag:useractionevent="{
            action: 'select',
            category: pageCategoryName,
            cta_name: article.titlePlainText,
            ecommerce: undefined,
            event: 'uaevent',
            event_detail: `${article.position}::${article.titlePlainText}`,
            event_name: 'read_article',
            label: `${article.position}::${article.titlePlainText}`,
          }"
          :class="['article-card', {'article-card--landing': landingArticles}]"
          :href="article.ctaLink"
          :title="article.image.alt"
        >
          <div
            v-if="hasImage(article)"
            class="image image--cover"
          >
            <img
              v-srcset="article.image.srcset"
              :alt="article.image.alt"
              class="image__item"
              :src="article.image.src"
            >
          </div>
          <div :class="{'article-card--landing__text': landingArticles}">
            <p
              v-if="!landingArticles"
              class="article-card__surtitle"
              v-text="article.surtitle"
            />
            <div class="article-card__title">
              <span v-html="article.title" />
            </div>
            <p
              v-lineclamp="3"
              class="article-card__description"
              v-html="article.description"
            />
            <p
              v-if="landingArticles"
              class="article-card--landing__cta-label"
            >
              {{ learnMoreLabel }}
              <svg class="icon">
                <use xlink:href="#chevron-right" />
              </svg>
            </p>
          </div>
        </a>
      </li>
    </ul>
    <div
      :aria-live="computedHasMoreToLoad ? 'polite' : 'off'"
      :class="['load-more-articles__loading', {'load-more-articles__loading--show' : loading}]"
    >
      <div v-if="loading">
        <span class="is-sr-only">{{ loadingStateLabel }}</span>
        <slot name="loading" />
      </div>
    </div>
    <div class="load-more-articles__cta">
      <button
        v-if="computedHasMoreToLoad"
        class="btn btn--ghost"
        @click="loadMoreArticles"
      >
        {{ loadMoreCtaLabel }}
      </button>
    </div>
  </div>
</template>

<script>
  import { ellipsis } from '@Foundation/directives/ellipsis/ellipsis';
  import { srcset } from '@Foundation/directives/srcset/srcset';
  import { lineclamp } from '@Foundation/directives/lineclamp/lineclamp';
  import { tag } from '@Foundation/analyticsHandler/directives/tag';
  import { eventBus } from '@Foundation/eventbus/eventbus';

  export default {
    name: 'LoadMoreArticles',
    directives: {
      ellipsis,
      srcset,
      lineclamp,
      tag
    },
    props: {
      apiUrl: {
        type: String,
        required: true
      },
      learnMoreLabel: {
        default: 'Learn More',
        type: String,
      },
      loadMoreCtaLabel: {
        type: String,
        required: false,
        default: 'Load More'
      },
      loadingStateLabel: {
        type: String,
        required: false,
        default: 'Loading more results'
      },
      hasMore: {
        type: Boolean,
        required: false,
        default: false
      },
      initialArticles: {
        type: Array,
        required: true,
        default: undefined
      },
      landingArticles: {
        type: Boolean,
        required: false,
        default: false
      },
      pageCategoryName: {
        type: String,
        required: false,
        default: ''
      },
    },
    data() {
      return {
        url: null,
        loadList: [],
        hasMoreArticlesToLoad: true,
        loading: false,
        loaded: false
      };
    },
    computed: {
      computedApiUrl() {
        return this.url ? this.url : this.apiUrl;
      },
      computedHasMoreToLoad() {
        return this.hasMoreArticlesToLoad !== undefined ? this.hasMoreArticlesToLoad : this.hasMore;
      }
    },
    mounted() {
      this.loadList = this.initialArticles || [];
      this.url = this.apiUrl;
      this.hasMoreArticlesToLoad = this.hasMore;
      this.loaded = true;
    },
    methods: {
      loadMoreArticles() {
        this.loading = true;
        const parseJSON = (response) => response.json();

        fetch(this.computedApiUrl)
          .then(parseJSON)
          .then(data => {
            eventBus.$emit('load-more-articles');
            this.url = data.newUrl;

            if (data.articles) {
              this.loadList = this.loadList.concat(data.articles);
              this.hasMoreArticlesToLoad = data.hasMore;
            }

            this.loading = false;
          })
          .catch(error => {
            this.loading = false;
            console.error('Could not load articles', error); // eslint-disable-line no-console
          });
      },
      hasImage(article) {
        return !!(article && article.image && article.image.src);
      }
    }
  };
</script>

<style src="./LoadMoreArticles.scss" lang="scss">
